import { useCommands } from "@components/commands/useCommands";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { SideNav } from "@design-system/SideNav/SideNav";
import { SideNavHeader } from "@design-system/SideNav/SideNavHeader";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { ClockIcon, PlusIcon } from "@icons";
import { getActionsTasksAccountsNavPaths } from "../getActionsTasksAccountsNavPaths";
import { useNavigationData } from "../hooks/useNavigationData";
import { NavigationShortcutRegistrar } from "../NavigationShortcutRegistrar";
import { useLiveTodoTasksCount } from "../useLiveTodoTasksCount";
import { useRecordRecentWorkspace } from "../useRecordRecentWorkspace";
import { AccountingSection } from "./AccountingSection";
import { ActionsRow } from "./ActionsRow";
import { AutomationSection } from "./AutomationSection";
import { DataSection } from "./DataSection";
import { PaymentsSection } from "./PaymentsSection";
import { ReportsSection } from "./ReportsSection";
import { SideNavPicker } from "./SideNavPicker";
import { useNavigationShortcutKeys } from "./useNavigationShortcutKeys";
import { useSideNavSections } from "./useSideNavSections";
import { WorkspaceSideNavFooter } from "./WorkspaceSideNavFooter";
import { WORKSPACE_SIDE_NAV_SECTIONS } from "./workspaceSideNavSections";

const WorkspaceActionsRow = () => {
    const { tasksTabPath } = getActionsTasksAccountsNavPaths();
    const todoTasksCount = useLiveTodoTasksCount();
    const shortcutKeys = useNavigationShortcutKeys();

    return (
        <ActionsRow
            to={tasksTabPath}
            end
            count={todoTasksCount}
            shortcut={shortcutKeys(tasksTabPath)}
        />
    );
};

/** The workspace sidebar on `SideNav`, mapped from today's redesigned nav. */
export const WorkspaceSideNav = () => {
    const { open: openCommands } = useCommands();
    const { hasBusinessEntities, hasMultipleWorkspaces, canUseConsolidation } =
        useNavigationData();
    const { sectionProps } = useSideNavSections({
        level: "workspace",
        sections: WORKSPACE_SIDE_NAV_SECTIONS,
    });
    useRecordRecentWorkspace();
    const shortcutKeys = useNavigationShortcutKeys();

    return (
        <>
            <NavigationShortcutRegistrar
                hasMultipleWorkspaces={hasMultipleWorkspaces}
            />
            <SideNav
                aria-label="Main"
                header={<SideNavHeader picker={<SideNavPicker />} />}
                top={
                    <>
                        <SideNavItem
                            onClick={() => openCommands()}
                            icon={<PlusIcon />}
                            label="New"
                            shortcut="mod+k"
                            trailing={<Shortcut keys="mod+k" size="sm" />}
                            trailingVisibility="reveal"
                        />
                        <WorkspaceActionsRow />
                        <SideNavItem
                            to="/activity"
                            icon={<ClockIcon />}
                            label="Activity"
                            shortcut={shortcutKeys("/activity")}
                        />
                    </>
                }
                footer={<WorkspaceSideNavFooter />}
            >
                <DataSection
                    section={sectionProps("Data")}
                    canUseConsolidation={canUseConsolidation}
                    hasBusinessEntities={hasBusinessEntities}
                />
                <ReportsSection
                    section={sectionProps("Reports")}
                    canUseConsolidation={canUseConsolidation}
                    hasBusinessEntities={hasBusinessEntities}
                />
                {hasBusinessEntities && (
                    <AccountingSection
                        section={sectionProps("Accounting")}
                        canUseConsolidation={canUseConsolidation}
                    />
                )}
                <AutomationSection section={sectionProps("Automation")} />
                <PaymentsSection section={sectionProps("Payments")} />
            </SideNav>
        </>
    );
};
